import { useState } from "react";
import { CheckCircle, Circle, CaretDown, CaretUp, Rocket } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { useOnboarding } from "@/hooks/useOnboarding";
import { ONBOARDING, ONBOARDING_FLOW, ONBOARDING_FLOW_PATHS } from "@/lib/onboarding-config";

export default function OnboardingChecklist() {
  const { isDone, markDone } = useOnboarding();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);

  const pages = ONBOARDING_FLOW.map(key => ({
    ...ONBOARDING[key],
    done: isDone(key),
    path: ONBOARDING_FLOW_PATHS[key],
  }));

  const completedCount = pages.filter(p => p.done).length;
  const allComplete = completedCount === pages.length;

  if (allComplete) return null;

  const pct = Math.round((completedCount / pages.length) * 100);

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition text-left"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-amber-50 dark:bg-amber-900/30 border-2 border-amber-200 dark:border-amber-700 flex items-center justify-center shrink-0">
            {allComplete
              ? <Rocket size={16} weight="fill" className="text-amber-500" />
              : <span className="text-amber-600 dark:text-amber-400 font-extrabold text-[11px] leading-none">{pct}%</span>
            }
          </div>
          <div>
            <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm">Complete your setup</p>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {completedCount} of {pages.length} steps done
            </p>
          </div>
        </div>
        {open
          ? <CaretUp size={15} className="text-slate-400 shrink-0" />
          : <CaretDown size={15} className="text-slate-400 shrink-0" />
        }
      </button>

      {open && (
        <>
          <div className="h-1 bg-slate-100 dark:bg-slate-700">
            <div
              className="h-full bg-amber-400 transition-all duration-500 rounded-r-full"
              style={{ width: `${pct}%` }}
            />
          </div>

          <ul className="divide-y divide-slate-50 dark:divide-slate-700/60">
            {pages.map((page) => (
              <li key={page.key}>
                <button
                  onClick={() => navigate(page.path)}
                  className="w-full flex items-center gap-3 px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-700/40 transition text-left group"
                >
                  {page.done
                    ? <CheckCircle size={18} weight="fill" className="text-green-500 shrink-0" />
                    : <Circle size={18} className="text-slate-300 dark:text-slate-600 shrink-0 group-hover:text-amber-300 transition" />
                  }
                  <span className={`text-sm flex-1 ${page.done ? "text-slate-400 dark:text-slate-500 line-through" : "text-slate-700 dark:text-slate-200 font-medium"}`}>
                    {page.title}
                  </span>
                  {!page.done && (
                    <span className="text-xs text-amber-500 font-semibold opacity-0 group-hover:opacity-100 transition">
                      Go →
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>

          <div className="px-5 py-3 border-t border-slate-100 dark:border-slate-700">
            <button
              onClick={() => {
                ONBOARDING_FLOW.forEach(k => markDone(k));
              }}
              className="text-xs text-slate-400 hover:text-slate-500 dark:hover:text-slate-300 transition"
            >
              Dismiss setup guide
            </button>
          </div>
        </>
      )}
    </div>
  );
}
